import React, { useContext, useEffect, useMemo, useCallback, useState } from "react";
import { RiDeleteBinLine } from "react-icons/ri";
import AuthUser from "../AuthUser";

import Drawer from "./Drawer";
import { toast } from "react-toastify";
import RightSidebar from "../Common/RightSidebar";
import "../../CSS/Layout.css";
import Select from "react-select";
import moment from "moment";
import { ReactSearchAutocomplete } from "react-search-autocomplete";
import { useQuery } from "react-query";
import Camera from "./Camera";
import { cashierLang } from "../language/pages/cashierPageLang";
import useLanguage from "../language/useLanguage";
import CashierNavbar from "../Common/CashierNavbar";
import ReactDatePicker from "react-datepicker";
import { appContext } from "../../navbar/auth";
import { useLocation } from "react-router-dom";

const CashierPerson = () => {
  const { http, user } = AuthUser();
  const lan = useLanguage(cashierLang);
  const alertToast = (text) => toast.error(text);
  const { data } = useQuery("current-stock", () =>
    http
      .get(`/current-stock-sales-counter`)
      .then((res) => res?.data?.data ?? res?.data ?? [])
  );
  const { data: mrpChannels, isLoading: channelsLoading } = useQuery(
    "mrp-master-channels",
    () =>
      http.get("mrp-master-channels").then((res) => {
        const out = res?.data?.data ?? res?.data ?? [];
        if (Array.isArray(out)) return out;
        if (out && typeof out === "object") return Object.values(out);
        return [];
      }),
    { staleTime: 5 * 60 * 1000 }
  );
  // Fallback: current-stock-sales-counter may not include drug.channel_mrp_prices in production; /drugs does
  const { data: drugsList } = useQuery("drugs-channel-mrp", () =>
    http.get("drugs").then((res) => {
      const out = res?.data?.data ?? res?.data ?? [];
      return Array.isArray(out) ? out : [];
    })
  );
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [billType, setBillType] = useState("customer");

  const stockList = Array.isArray(data) ? data : [];
  const drugIdToChannelMrp = useMemo(() => {
    const list = drugsList ?? [];
    const map = {};
    list.forEach((d) => {
      const id = d.id ?? d.drug_id;
      if (id == null) return;
      const arr = d.channel_mrp_prices ?? d.mrp_prices ?? [];
      map[id] = Array.isArray(arr) ? arr : typeof arr === "object" && arr !== null ? Object.values(arr) : [];
    });
    return map;
  }, [drugsList]);
  const normalizeChannelName = useCallback((val) => {
    return String(val ?? "")
      .trim()
      .toLowerCase();
  }, []);
  const selectedChannelKey = useMemo(() => {
    return normalizeChannelName(selectedChannel?.channel_name);
  }, [normalizeChannelName, selectedChannel]);
  const getItemChannelPrices = useCallback(
    (item) => {
      let raw =
        item?.drug?.channel_mrp_prices ??
        item?.drug?.mrp_prices ??
        item?.channel_mrp_prices ??
        item?.mrp_prices;
      if (!raw || (Array.isArray(raw) && raw.length === 0)) {
        const drugId = item?.drug_id ?? item?.drug?.id;
        raw = drugId != null ? drugIdToChannelMrp[drugId] : [];
      }
      if (Array.isArray(raw)) return raw;
      if (raw && typeof raw === "object") return Object.values(raw);
      return [];
    },
    [drugIdToChannelMrp]
  );
  const productsByChannel = useMemo(() => {
    if (!selectedChannelKey) return stockList;
    return stockList.filter((item) => {
      const channels = getItemChannelPrices(item);
      return channels.some(
        (ch) => normalizeChannelName(ch?.channel_name) === selectedChannelKey
      );
    });
  }, [stockList, getItemChannelPrices, normalizeChannelName, selectedChannelKey]);

  const { data: customers } = useQuery(billType, () =>
    http.get(`/customers/${billType}`).then((res) => res?.data?.data)
  );
  const { pathname } = useLocation();
  const {
    cart,
    setCart,
    setAllSubTotal,
    setAllSubTotalWithoutDiscount,
    setInvoice,
    setStickerCart,
    invoiceReload,
    setInvoiceReload,
    invoiceId,
    setInvoiceId,
    setPaymentMethod,
    setSpecialDiscount,
    setReturnAmount,
    updateTrack,
    setInvoicePaidUnpaid,
    paymentStatus,
    setPaymentStatus,
    autoReloadInvoice,
    setPrescriptionImage,
    setPaidAmount,
    selectedInvoice,
    setSelectedInvoice,
  } = useContext(appContext);

  useEffect(() => {
    http.get(`/last-invoice`).then(async (res) => {
      if (res?.data?.data) {
        const rendomNumber = `INV-${res.data.data?.id + 10001}`;
        setInvoice(rendomNumber);

        setPaidAmount();
      } else {
        setInvoice("INV-10001");
        setPaidAmount();
      }
    });
  }, [invoiceReload]);

  // End of Select with check box for printing stickers
  const [invoiceData, setInvoiceData] = useState({
    customer_type: billType,
    date: new Date(),
    delivery_charge: "",
    last_due: "",
    last_due_date: "",
    member_id: "",
    receiver_name: "",
    receiver_address: "",
    previous_due: "",
    previous_due_date: "",
    transport: "",
    due: "",
  });
  const boxQtyHandler = (item, e) => {
    const existCart = [...cart];
    existCart.map((pd) => {
      if (pd.id === item.id) {
        const temp = e.target.value;
        if (!temp) {
          pd.noOfBox = 0;
          pd.pcs = "";
          pd.totalPrice = 0;
          pd.totalPriceWithoutDiscount = 0;
        } else {
          if (parseFloat(temp) <= item.stock || parseFloat(temp) <= item.qty) {
            const test = parseFloat(temp / parseFloat(item.pktSize));
            // console.log("Calculated data",test);
            pd.noOfBox = test;
            pd.pcs = parseFloat(temp);
            pd.totalPrice = parseFloat(pd?.price || 0) * parseFloat(pd.pcs);
            pd.totalPriceWithoutDiscount =
              parseFloat(pd?.price || 0) * parseFloat(pd.pcs);
          } else {
            alertToast(
              `Not available more than ${item.stock ? item.stock : item.qty}`
            );
          }
        }
      }
    });
    setCart(existCart);
  };
  const priceHandler = (item, e) => {
    const existCart = [...cart];
    existCart.map((pd) => {
      if (pd.id === item.id) {
        const temp = e.target.value;
        if (!temp) {
          pd.price = "";
          pd.totalPrice = 0;
          pd.totalPriceWithoutDiscount = 0;
        } else {
          pd.price = parseFloat(temp);
          pd.totalPrice = parseFloat(pd?.price || 0) * parseFloat(pd.pcs || 0);
          pd.totalPriceWithoutDiscount =
            parseFloat(pd?.price || 0) * parseFloat(pd.pcs || 0);
        }
      }
    });
    setCart(existCart);
  };

  // When channel is selected, fill price from that channel's MRP
  const channelSelectHandler = (item, channelId) => {
    const channels = item?.drug?.channel_mrp_prices || [];
    const selected = channels.find(
      (ch) => String(ch.id) === String(channelId)
    );
    const existCart = [...cart];
    existCart.forEach((pd) => {
      if (pd.id === item.id) {
        pd.selectedChannelId = channelId ? String(channelId) : null;
        pd.selectedChannelName = selected ? selected.channel_name : "";
        if (selected && selected.mrp_price != null) {
          pd.price = parseFloat(selected.mrp_price);
          pd.totalPrice = parseFloat(pd.price) * parseFloat(pd.pcs || 0);
          pd.totalPriceWithoutDiscount = parseFloat(pd.price) * parseFloat(pd.pcs || 0);
        } else {
          pd.price = "";
          pd.totalPrice = 0;
          pd.totalPriceWithoutDiscount = 0;
        }
      }
    });
    setCart(existCart);
  };
  useEffect(() => {
    if (cart.length > 0) {
      const subTotal = cart.reduce(
        (previousValue, currentValue) =>
          previousValue + parseFloat(currentValue.totalPrice),
        0
      );
      const subTotalNoDisc = cart.reduce(
        (previousValue, currentValue) =>
          previousValue + parseFloat(currentValue.totalPrice),
        0
      );
      const subWithDelivery =
        subTotal + parseFloat(invoiceData?.delivery_charge || 0);
      const subNoDisc =
        subTotalNoDisc + parseFloat(invoiceData?.delivery_charge || 0);
      setAllSubTotal(parseFloat(subWithDelivery).toFixed(2));
      setAllSubTotalWithoutDiscount(parseFloat(subNoDisc).toFixed(2));
      setStickerCart(cart);
    }
  }, [cart, invoiceData?.delivery_charge]);

  // Getting All invoices
  const [allInvoices, setAllInvoices] = useState([]);
  const [paidInvoices, setPaidInvoices] = useState([]);
  const [unPaidInvoices, setUnPaidInvoices] = useState([]);

  useEffect(() => {
    http.get("invoice").then((res) => {
      setAllInvoices(res.data.data);
      setInvoicePaidUnpaid(res.data.data);

      setPaidInvoices(
        res.data.data.filter((item) => item.payment_status === "Paid")
      );
      setUnPaidInvoices(
        res.data.data.filter((item) => item.payment_status === "Unpaid")
      );
    });
  }, [updateTrack, autoReloadInvoice]);

  const handleIVInput = (e) => {
    // setPurchaseOrderId(e.id);
    setInvoiceId(e.id);
  };
  // End of getting All invoices
  const [pImage, setPImage] = useState("");
  // get selected invoice
  useEffect(() => {
    if (invoiceId !== null) {
      http.get(`view-selected-invoice/${invoiceId}`).then((res) => {
        if (res.data.status === 200) {
          const invoiceData = res.data.data;
          setSelectedInvoice(invoiceData);
          setBillType(invoiceData?.customer_type || "customer");
          setPaymentMethod(invoiceData?.discount_type);
          setAllSubTotalWithoutDiscount(invoiceData?.sub_total);
          //set paid unpaid status
          setPaymentStatus(invoiceData.payment_status);
          // Normalize invoice_details so cart has drug object, image, and MRP channel for preview
          const details = res.data.invoice_details || [];
          const normalizedCart = details.map((row) => ({
            ...row,
            // Use selling price from API (invoice line price)
            price: row.sellingPrice != null ? row.sellingPrice : row.price,
            pcs: row.pcs ?? row.qty,
            totalPrice: row.totalPrice ?? row.total_price,
            // Nested drug for components that expect item.drug
            drug: {
              ...(row.drug || {}),
              unit: row.unit ?? row.drug?.unit,
              macrohealth_sg: row.macrohealth_sg ?? row.name ?? row.drug?.macrohealth_sg,
              main_image: row.main_image ?? row.drug?.main_image,
              image: row.main_image ?? row.image ?? row.drug?.main_image ?? row.drug?.image,
            },
            // MRP channel for invoice preview (API returns mrp_channel_name)
            selectedChannelName: row.mrp_channel_name ?? row.selectedChannelName,
          }));
          setCart(normalizedCart);
          setInvoice(invoiceData.invoice_no);
          setPImage(invoiceData.prescription_image);
          setSpecialDiscount(invoiceData.special_discount);
          
          // Populate invoiceData with invoice information
          setInvoiceData({
            customer_type: invoiceData?.customer_type || "customer",
            date: invoiceData?.date ? moment(invoiceData.date).toDate() : new Date(),
            delivery_charge: invoiceData?.delivery_charge || "",
            last_due: invoiceData?.last_due || "",
            last_due_date: invoiceData?.last_due_date || "",
            member_id: invoiceData?.member_id ?? invoiceData?.customer_id ?? "",
            name: invoiceData?.member_name || invoiceData?.name || "",
            address: invoiceData?.address || "",
            mobile: invoiceData?.mobile || invoiceData?.member_phone || "",
            receiver_name: invoiceData?.receiver_name || "",
            receiver_address: invoiceData?.receiver_address || "",
            previous_due: invoiceData?.previous_due || "",
            previous_due_date: invoiceData?.previous_due_date 
              ? moment(invoiceData.previous_due_date).toDate() 
              : null,
            transport: invoiceData?.transport || "",
            due: invoiceData?.due || invoiceData?.present_due || invoiceData?.due_amount || "",
          });
        } else {
          // setError(res.data.errors);
        }
      });
    }
  }, [invoiceId]);

  // end of select invoice
  const removeMedicine = (item) => {
    const existCart = [...cart];
    const newCart = existCart.filter((pd) => pd.id !== item.id);
    setCart(newCart);
  };

  //   Clear all information
  const [searchMedicine, setSearchMedicine] = useState("");
  const clearInfo = () => {
    setCart([]);
    setInvoiceReload(!invoiceReload);
    //
    setInvoiceId();
    setSpecialDiscount(0);
    setReturnAmount(0);
    setPaymentMethod("Cash");
    setPaymentStatus("Default");
    setPImage("");
    setSearchMedicine("");
    setSelectedInvoice({});
    setInvoiceData({
      customer_type: "customer",
      date: new Date(),
      delivery_charge: "",
      last_due: "",
      last_due_date: "",
      member_id: "",
      receiver_name: "",
      receiver_address: "",
      previous_due: "",
      previous_due_date: "",
      transport: "",
      due: "",
    });
  };

  const formatResult = (item) => {
    const drugImageUrl = getDrugImageUrl(item);
    return (
      <>
        <div
          className={`row d-flex align-items-center search-format ${
            item.stock < 1 && Number(item?.is_stockable) === 1
              ? "bg-red text-white"
              : ""
          } `}
        >
          <div className="col-9 d-flex align-items-center gap-2">
            <div
              className="position-relative flex-shrink-0 cashier-search-result-img-wrap"
              style={{ width: "48px", height: "48px", borderRadius: "6px", overflow: "hidden", border: "1px solid #e9ecef", backgroundColor: "#f8f9fa" }}
              onClick={(e) => e.stopPropagation()}
            >
              {drugImageUrl ? (
                <>
                  <img
                    src={drugImageUrl}
                    alt={item?.drug?.macrohealth_sg || ""}
                    style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                    onError={(e) => {
                      e.target.style.display = "none";
                      if (e.target.nextElementSibling) e.target.nextElementSibling.style.display = "flex";
                    }}
                  />
                  <button
                    type="button"
                    className="border-0 rounded-circle position-absolute d-flex align-items-center justify-content-center cashier-zoom-btn"
                    style={{
                      top: "2px", right: "2px", width: "22px", height: "22px",
                      backgroundColor: "rgba(0,0,0,0.5)", color: "#fff", cursor: "pointer",
                      opacity: 0, transition: "opacity 0.2s", padding: 0
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.opacity = 1; }}
                    onMouseLeave={(e) => { e.currentTarget.style.opacity = 0; }}
                    onClick={(e) => { e.stopPropagation(); setZoomImage(drugImageUrl); }}
                    title="Zoom"
                  >
                    <svg style={{ width: "12px", height: "12px" }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7" />
                    </svg>
                  </button>
                </>
              ) : (
                <div className="w-100 h-100 d-flex align-items-center justify-content-center" style={{ fontSize: "20px" }}>📦</div>
              )}
            </div>
            <p className="mb-0">{item?.drug?.macrohealth_sg}</p>
          </div>
          <div className="col-3 ">
            <div className="row">
              <div className="col-7">
                <p>{Number(item?.is_stockable) === 1 ? item?.stock : ""}</p>
              </div>
              <div className="col-3">
                <i
                  style={{ fontSize: "12px" }}
                  className={`fas fa-circle ${
                    item.stock < 1 && Number(item?.is_stockable) === 1
                      ? "text-danger"
                      : Number(item?.is_stockable) === 0
                      ? "text-warning"
                      : "text-success"
                  }`}
                ></i>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  };

  const medicineSelect = (item) => {
    if (item.stock < 1) {
      toast.error("This product is out of stock");
      return;
    } else {
      let alreadyExist = false;
      const newCart = [...cart];

      newCart.map((pd) => {
        if (pd.id === item.id) {
          alreadyExist = true;
        }
      });

      if (!alreadyExist) {
        const channelPrices = getItemChannelPrices(item);
        const channelForPrice =
          selectedChannelKey &&
          channelPrices.find((ch) => normalizeChannelName(ch?.channel_name) === selectedChannelKey);
        const firstChannel = channelForPrice ?? (channelPrices.length === 1 ? channelPrices[0] : null);
        const defaultPrice = firstChannel != null ? parseFloat(firstChannel.mrp_price) : "";
        const defaultChannelId = firstChannel ? String(firstChannel.id) : null;
        const defaultChannelName = firstChannel ? firstChannel.channel_name : "";
        newCart.push({
          ...item,
          noOfBox: 0,
          pcs: "",
          pktSize: item.drug.pktSize,
          boxType: item.drug.boxType,
          expire_date: item.drug.expiry_date,
          totalPrice: 0,
          price: defaultPrice,
          totalPriceWithoutDiscount: 0,
          selectedChannelId: defaultChannelId,
          selectedChannelName: defaultChannelName,
        });
      }
      setCart(newCart);
      setSearchMedicine("");
    }
  };
  const imageURL = global.img_Url;
  const prescriptionImage = document.getElementById("prescription-image");

  // Product main_image URL (same as Drugs list: /files/drugs/{filename})
  const baseImageUrl = useMemo(() => {
    const url = process.env.REACT_APP_DEV_MODE_IMAGE_URL ||
      process.env.REACT_APP_IMAGE_URL ||
      process.env.REACT_APP_PRO_MODE_IMAGE_URL ||
      "http://127.0.0.1:8001";
    return url.replace(/\/$/, "");
  }, []);

  const getDrugImageUrl = useCallback((item) => {
    const drug = item?.drug != null ? item.drug : item;
    if (!drug) return null;
    let imageFileName = drug.main_image || drug.image || "";
    if (!imageFileName || imageFileName === "null" || imageFileName === "undefined" || String(imageFileName).trim() === "") {
      return null;
    }
    imageFileName = String(imageFileName).replace(/^.*[\\/]/, "").split("?")[0].split("#")[0].trim();
    return imageFileName ? `${baseImageUrl}/files/drugs/${imageFileName}` : null;
  }, [baseImageUrl]);

  const [zoomImage, setZoomImage] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setInvoiceData({
      ...invoiceData,
      [name]: value,
    });
  };
  console.log("invoiceData", invoiceData);
  return (
    <>
      <div style={{ minHeight: "90dvh" }} className={`container-fluid`}>
        <CashierNavbar />
        <div style={{ marginTop: "70px" }}>
          <div className="row mb-2">
            <div className="col-8">
              <div className="custom-card p-1">
                <div className="row">
                  <div className="col-3">
                    <label className="invoice-label"> Date</label>
                    <ReactDatePicker
                      className="form-control form-control-sm"
                      selected={
                        invoiceData?.date ? invoiceData?.date : new Date()
                      }
                      dateFormat="dd/MM/yyyy"
                      onChange={(date) => {
                        setInvoiceData({
                          ...invoiceData,
                          date: date,
                        });
                      }}
                    />
                  </div>
                  <div className="col-3">
                    <label className="invoice-label"> Bill Type</label>
                    <Select
                      options={[
                        { value: "customer", label: "Customer" },
                        { value: "dealer", label: "Dealer" },
                      ]}
                      placeholder={lan?.select}
                      isDisabled={!!selectedInvoice?.id}
                      onChange={(e) => {
                        setBillType(e.value);
                      }}
                      value={{
                        value: billType,
                        label: billType === "customer" ? "Customer" : "Dealer",
                      }}
                    />
                  </div>
                  <div className="col-6">
                    <label className="invoice-label">
                      {" "}
                      Select {billType === "customer" ? "Customer" : "Dealer"}
                    </label>

                    <div className="">
                      <Select
                        options={customers}
                        placeholder={lan?.select}
                        isDisabled={!!selectedInvoice?.id}
                        onChange={(e) =>
                          setInvoiceData({
                            ...invoiceData,
                            member_id: e.id,
                            name: e.member_name,
                            address: e.address,
                            mobile: e.mobile,
                            due: parseFloat(e?.due || 0),
                            last_due: e?.last_invoice?.present_due || 0,
                            last_due_date: e?.last_invoice?.date
                              ? moment(e?.last_invoice?.date).format(
                                  "DD/MM/YYYY"
                                )
                              : "",
                            receiver_name: e?.receiver_name || e?.member_name || "",
                            receiver_address: e?.receiver_address || e?.address || "",
                            previous_due: e?.previous_due || e?.last_invoice?.present_due || "",
                            previous_due_date: e?.previous_due_date 
                              ? moment(e.previous_due_date).toDate()
                              : e?.last_invoice?.date
                              ? moment(e.last_invoice.date).toDate()
                              : null,
                            transport: e?.transport || "",
                          })
                        }
                        getOptionLabel={(unPaidInvoices) =>
                          `${unPaidInvoices.member_name}`
                        }
                        getOptionValue={(unPaidInvoices) =>
                          `${unPaidInvoices.id}`
                        }
                        value={
                          customers?.find(
                            (c) => String(c.id) === String(invoiceData?.member_id)
                          ) ?? null
                        }
                      />
                    </div>
                  </div>
                </div>
                {/* Channel selector and search box in same row */}
                <div className="search-container pb-2 row mt-2">
                  <div className="col-auto pe-2 d-flex align-items-center">
                    <select
                      className="form-select form-select-sm"
                      value={selectedChannel ? selectedChannel.id : ""}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (!val) {
                          setSelectedChannel(null);
                          return;
                        }
                        const ch = (mrpChannels || []).find(
                          (c) => String(c.id) === String(val)
                        );
                        setSelectedChannel(
                          ch ? { id: ch.id, channel_name: ch.channel_name } : null
                        );
                      }}
                      disabled={channelsLoading || !!selectedInvoice?.id}
                      style={{ minWidth: "160px", height: "36px" }}
                      title={selectedInvoice?.id ? (lan?.channelName ?? "MRP Channel") + " (read-only for selected invoice)" : undefined}
                    >
                      <option value="">
                        {channelsLoading
                          ? (lan?.loading ?? "Loading...")
                          : (lan?.select ?? "Select channel")}
                      </option>
                      {!channelsLoading &&
                        (mrpChannels || []).map((ch) => (
                          <option key={ch.id} value={ch.id}>
                            {ch.channel_name}
                          </option>
                        ))}
                    </select>
                  </div>
                  <div className="search-box-container col">
                    {/* </div> */}
                    <ReactSearchAutocomplete
                      showIcon={false}
                      placeholder={lan?.searchProducts}
                      inputSearchString={searchMedicine || ""}
                      onSearch={(value) => setSearchMedicine(value)}
                      items={productsByChannel}
                      resultStringKeyName="name"
                      maxResults={5}
                      onSelect={(item) => medicineSelect(item)}
                      autoFocus
                      fuseOptions={{ keys: ["name"] }} // Search in the description text as well
                      styling={{
                        borderRadius: "5px !important",
                        // zIndex: user?.user_type === "cashier" ? 0 : 0,
                        minHeight: "36px",
                        position: "static",
                        height: "36px",
                        fontSize: "13px",
                      }}
                      formatResult={formatResult}
                    />
                  </div>

                  <div className="col-md-3 col-sm-12 d-flex search-invoice-section">
                    <div>
                      <button
                        className="btn btn-primary btn-sm rounded px-2 mt-1"
                        onClick={clearInfo}
                      >
                        {lan?.clear}
                      </button>
                    </div>
                    {/* <h6 className="mx-sm-2 text-xs mt-sm-2  mx-md-3 mt-1 whitespace-nowrap lg:text-[14px]">
                      {lan?.invoiceNo} : {cart.length > 0 && invoice}
                    </h6> */}
                    {(user?.user_type === "cashier" ||
                      user?.user_type === "admin" ||
                      user?.user_type === "manager") && (
                      <Drawer
                        allInvoices={allInvoices}
                        paidInvoices={paidInvoices}
                        unPaidInvoices={unPaidInvoices}
                      />
                    )}
                  </div>
                </div>
                {/* prescription upload  */}
                <div className="prescription-upload pb-1 mt-2">
                  <div className="row">
                    <div className={`col-12`}>
                      <div className="row">
                        <div className="col-2 text-start">
                          <p className="mt-1 mb-0 text-lg fw-bold lg:text-[14px] whitespace-nowrap ms-1 ">
                            {lan?.documentUpload}
                          </p>
                        </div>
                        <div className="col-4">
                          <input
                            onChange={(e) =>
                              setPrescriptionImage(e.target.files[0])
                            }
                            type="file"
                            accept="image/*"
                            id="prescription-image"
                            className="form-control form-control-sm ms-1"
                          />
                        </div>
                        <div className="col-1">
                          {/* <FaCamera color='#69B128' size={20} /> */}
                          <Camera setPrescriptionImage={setPrescriptionImage} />
                        </div>
                        {/* <div className="col-5 d-flex">
                          {user.user_type === "cashier" && (
                            <div className="w-50">
                              <Select
                                options={unPaidInvoices}
                                placeholder={lan?.select}
                                onChange={handleIVInput}
                                getOptionLabel={(unPaidInvoices) =>
                                  `${unPaidInvoices.invoice_no}`
                                }
                                getOptionValue={(unPaidInvoices) =>
                                  `${unPaidInvoices.invoice_no}`
                                }
                              />
                            </div>
                          )}
                          {invoiceId && (
                            <p
                              style={{ background: "#e4f1a3" }}
                              className="px-3 py-1 ms-1 rounded-pill fw-bold"
                            >
                              {paymentStatus}
                            </p>
                          )}
                        </div> */}
                      </div>
                    </div>
                  </div>
                </div>

                {/* medicine cart  */}

                <>
                  <div
                    style={{ marginLeft: ".85rem", marginRight: ".85rem" }}
                    className="cart-container mt-2"
                  >
                    <div className="row">
                      <table className="cart-table">
                        <thead>
                        <tr className="cart-table-head">
  <th width={"25%"}>{lan?.productName}</th>
  <th className="text-center" width={"10%"}>{lan?.unit}</th>
  <th className="text-end" width={"10%"}>{lan?.quantity}</th>
  <th className="text-end" width={"15%"}>{lan?.price}</th>
  <th className="text-end" width={"15%"}>{lan?.total}</th>
  {!invoiceId && <th className="text-center" width={"8%"}>{lan?.action}</th>}
</tr>
                        </thead>
                        <tbody>
  {invoiceId ? (
  <>
    {cart.length > 0 &&
      cart.map((item, i) => {
        const drugImgUrl = getDrugImageUrl(item);
        const productName = item.macrohealth_sg ?? item.name ?? item?.drug?.macrohealth_sg ?? "";
        return (
        <tr key={i} className="border-bottom cart-table-body">
          <td width={"25%"}>
            <div className="d-flex align-items-center gap-2">
              <div
                className="position-relative flex-shrink-0 cashier-cart-product-img-wrap"
                style={{
                  width: "48px",
                  height: "48px",
                  borderRadius: "6px",
                  overflow: "hidden",
                  border: "1px solid #e9ecef",
                  backgroundColor: "#f8f9fa",
                }}
              >
                {drugImgUrl ? (
                  <>
                    <img
                      src={drugImgUrl}
                      alt={productName}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        display: "block",
                      }}
                      onError={(e) => {
                        e.target.style.display = "none";
                        if (e.target.nextElementSibling) e.target.nextElementSibling.style.display = "flex";
                      }}
                    />
                    <div
                      className="w-100 h-100 d-flex align-items-center justify-content-center"
                      style={{ fontSize: "20px", display: "none" }}
                    >
                      📦
                    </div>
                  </>
                ) : (
                  <div
                    className="w-100 h-100 d-flex align-items-center justify-content-center"
                    style={{ fontSize: "20px" }}
                  >
                    📦
                  </div>
                )}
              </div>
              <span>{productName}</span>
            </div>
          </td>
          <td className="text-center" width={"10%"}>{item?.unit ?? item?.drug?.unit}</td>
          <td className="text-end" width={"10%"}>{item.pcs}</td>
          <td className="text-end" width={"15%"}>{parseFloat(item?.price).toFixed(2)}</td>
          <td className="text-end" width={"15%"}>{parseFloat(item?.totalPrice).toFixed(2)}</td>
        </tr>
        );
      })}

    {/* Selected invoice summary (when invoice is loaded from All Invoice) */}
    {selectedInvoice?.id && (
      <>
        <tr className="invoice-border-dashed-top">
          <td colSpan={4} className="text-end fw-bolder">Delivery Charge</td>
          <td className="text-end" width={"15%"}>
            {parseFloat(selectedInvoice?.delivery_charge || 0).toFixed(2)}
          </td>
        </tr>
        <tr>
          <td colSpan={4} className="text-end fw-bolder">Special Discount</td>
          <td className="text-end" width={"15%"}>
            {parseFloat(selectedInvoice?.special_discount || 0).toFixed(2)}
          </td>
        </tr>
        <tr className="invoice-border-dashed-top">
          <td colSpan={4} className="text-end fw-bolder">Grand Total</td>
          <td className="text-end" width={"15%"}>
            {parseFloat(
              selectedInvoice?.grand_total ??
                (parseFloat(selectedInvoice?.sub_total || 0) -
                  parseFloat(selectedInvoice?.special_discount || 0))
            ).toFixed(2)}
          </td>
        </tr>
      </>
    )}
  </>
) : (
  <>
    {cart?.length > 0 &&
      cart?.map((item, i) => (
        <tr key={i} className="border-bottom cart-table-body">
          <td width={"25%"}>
            <div className="d-flex align-items-center gap-2">
              {(() => {
                const drugImgUrl = getDrugImageUrl(item);
                return (
                  <div
                    className="position-relative flex-shrink-0 cashier-cart-product-img-wrap"
                    style={{
                      width: "48px",
                      height: "48px",
                      borderRadius: "6px",
                      overflow: "hidden",
                      border: "1px solid #e9ecef",
                      backgroundColor: "#f8f9fa",
                    }}
                  >
                    {drugImgUrl ? (
                      <>
                        <img
                          src={drugImgUrl}
                          alt={item?.drug?.macrohealth_sg || ""}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            display: "block",
                          }}
                          onError={(e) => {
                            e.target.style.display = "none";
                            if (e.target.nextElementSibling)
                              e.target.nextElementSibling.style.display = "flex";
                          }}
                        />
                        <button
                          type="button"
                          className="border-0 rounded-circle position-absolute d-flex align-items-center justify-content-center cashier-zoom-btn"
                          style={{
                            top: "2px",
                            right: "2px",
                            width: "22px",
                            height: "22px",
                            backgroundColor: "rgba(0,0,0,0.5)",
                            color: "#fff",
                            cursor: "pointer",
                            opacity: 0,
                            transition: "opacity 0.2s",
                            padding: 0,
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.opacity = 1;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.opacity = 0;
                          }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setZoomImage(drugImgUrl);
                          }}
                          title="Zoom"
                        >
                          <svg
                            style={{ width: "12px", height: "12px" }}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7"
                            />
                          </svg>
                        </button>
                      </>
                    ) : (
                      <div
                        className="w-100 h-100 d-flex align-items-center justify-content-center"
                        style={{ fontSize: "20px" }}
                      >
                        📦
                      </div>
                    )}
                  </div>
                );
              })()}
              <span>{item?.drug?.macrohealth_sg}</span>
            </div>
          </td>

          <td className="text-center" width={"10%"}>{item?.drug?.unit}</td>

          {paymentStatus !== "Paid" ? (
            <td width={"10%"}>
              <div style={{ width: "100px", marginLeft: "auto" }}>
                <input
                  value={item?.pcs}
                  onChange={(e) => boxQtyHandler(item, e)}
                  className="form-control form-control-sm text-end"
                  type="number"
                />
              </div>
            </td>
          ) : (
            <td className="text-end" width={"10%"}>{item?.pcs}</td>
          )}

          <td width={"15%"}>
            <div style={{ width: "100px", marginLeft: "auto" }}>
              <input
                value={item?.price ?? ""}
                readOnly
                className="form-control form-control-sm bg-light text-end"
                type="text"
                style={{ cursor: "default" }}
              />
            </div>
          </td>

          <td className="text-end" width={"15%"}>{Number(item?.totalPrice).toFixed(2)}</td>

          <td width={"8%"}>
            <RiDeleteBinLine
              onClick={() => removeMedicine(item)}
              style={{
                fontSize: "16px",
                marginRight: "4px",
                cursor: "pointer",
              }}
              title="Delete Item"
            />
          </td>
        </tr>
      ))}
  </>
)}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>

                {/* uploaded image  */}
                {pImage && pImage !== "null" && (
                  <div className="custom-card m-3 p-3">
                    <h3 className="text-center"> Prescription </h3>
                    {pImage?.endsWith(".jpg") ||
                    pImage?.endsWith(".png") ||
                    pImage?.endsWith(".jpeg") ||
                    pImage?.endsWith(".webp") ||
                    pImage?.endsWith(".gif") ? (
                      <img
                        src={`${imageURL}/images/files/${pImage}`}
                        className="prescription-img"
                        alt="prescription"
                      />
                    ) : (
                      <img
                        src={pImage}
                        className="prescription-img"
                        alt="prescription"
                      />
                    )}
                  </div>
                )}

                <RightSidebar
                  pImage={prescriptionImage}
                  setSearchMedicine={setSearchMedicine}
                  setPImage={setPImage}
                  invoiceData={invoiceData}
                  setInvoiceData={setInvoiceData}
                />
              </div>
            </div>
            <div className="col-4">
              <div className="custom-card p-2">
                {pathname === "/cashier" && (
                  <div className="d-flex">
                    <div className="me-1" style={{ width: "30%" }}>
                      <label className="invoice-label"> Total Due</label>
                      <input
                        className="form-control form-control-sm"
                        value={invoiceData?.due}
                        readOnly
                      />
                    </div>
                    <div className="me-1" style={{ width: "30%" }}>
                      <label className="invoice-label"> Last Dues Date</label>
                      <input
                        className="form-control form-control-sm"
                        // onChange={handleChange}
                        name="last_dues_date"
                        value={invoiceData?.last_due_date}
                        readOnly
                      />
                    </div>
                    <div className="" style={{ width: "40%" }}>
                      <label className="invoice-label">Last Dues Amount</label>
                      <input
                        className="form-control form-control-sm"
                        // onChange={handleChange}
                        name="last_dues_amount"
                        value={invoiceData?.last_due}
                        readOnly
                      />
                    </div>
                  </div>
                )}

                <div className="row mb-2">
                  {pathname !== "/cashier" && (
                    <>
                      <div className="col-4">
                        <label className="invoice-label"> Total Due</label>
                        <input
                          className="form-control form-control-sm"
                          value={invoiceData?.due}
                          readOnly
                        />
                      </div>
                      <div className="col-4">
                        <label className="invoice-label"> Last Dues Date</label>
                        <input
                          className="form-control form-control-sm"
                          // onChange={handleChange}
                          name="last_dues_date"
                          value={invoiceData?.last_due_date}
                          readOnly
                        />
                      </div>
                      <div className="col-4">
                        <label className="invoice-label">
                          Last Dues Amount
                        </label>
                        <input
                          className="form-control form-control-sm"
                          // onChange={handleChange}
                          name="last_dues_amount"
                          value={invoiceData?.last_due}
                          readOnly
                        />
                      </div>
                    </>
                  )}
                  <div className="col-6">
                    <label className="invoice-label"> Receiver Name</label>
                    <input
                      className="form-control form-control-sm"
                      onChange={handleChange}
                      name="receiver_name"
                      value={invoiceData?.receiver_name || ""}
                      readOnly={!!invoiceId}
                    />
                  </div>
                  <div className="col-6">
                    <label className="invoice-label"> Receiver Address</label>
                    <input
                      className="form-control form-control-sm"
                      onChange={handleChange}
                      name="receiver_address"
                      value={invoiceData?.receiver_address || ""}
                      readOnly={!!invoiceId}
                    />
                  </div>
                  <div className="col-6">
                    <label className="invoice-label"> Previous Due Date</label>
                    {invoiceId ? (
                      <input
                        className="form-control form-control-sm"
                        readOnly
                        value={
                          invoiceData?.previous_due_date
                            ? moment(invoiceData.previous_due_date).format("DD/MM/YYYY")
                            : ""
                        }
                      />
                    ) : (
                      <ReactDatePicker
                        className="form-control form-control-sm"
                        selected={
                          invoiceData?.previous_due_date
                            ? invoiceData?.previous_due_date
                            : null
                        }
                        dateFormat="dd/MM/yyyy"
                        onChange={(date) => {
                          setInvoiceData({
                            ...invoiceData,
                            previous_due_date: date,
                          });
                        }}
                        placeholderText="DD/MM/YYYY"
                      />
                    )}
                  </div>
                  <div className="col-6">
                    <label className="invoice-label">
                      {" "}
                      Previous Due Amount
                    </label>
                    <input
                      className="form-control form-control-sm"
                      onChange={handleChange}
                      name="previous_due"
                      value={invoiceData?.previous_due || ""}
                      readOnly={!!invoiceId}
                    />
                  </div>
                  {/* <div className="col-6">
                    <label className="invoice-label"> Delivery Charge</label>
                    <input
                      className="form-control form-control-sm"
                      onChange={handleChange}
                      name="delivery_charge"
                    />
                  </div> */}

                  <div className="col-6">
                    <label className="invoice-label">Transport</label>
                    <input
                      className="form-control form-control-sm"
                      onChange={handleChange}
                      name="transport"
                      value={invoiceData?.transport || ""}
                      readOnly={!!invoiceId}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Zoom Image Modal - same as Drugs list */}
      {zoomImage && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.3)",
            backdropFilter: "blur(4px)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "16px",
          }}
          onClick={() => setZoomImage(null)}
        >
          <div
            style={{
              position: "relative",
              maxWidth: "90vw",
              maxHeight: "90vh",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "40px 16px 16px 16px",
            }}
          >
            <button
              type="button"
              onClick={() => setZoomImage(null)}
              style={{
                position: "absolute",
                top: "16px",
                right: "16px",
                backgroundColor: "white",
                color: "#333",
                border: "none",
                borderRadius: "50%",
                width: "40px",
                height: "40px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "background-color 0.2s",
                zIndex: 10,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#e9ecef";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "white";
              }}
              title="Close"
            >
              <svg style={{ width: "24px", height: "24px" }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <img
              src={zoomImage}
              alt="Product zoom"
              style={{
                maxWidth: "90vw",
                maxHeight: "90vh",
                width: "auto",
                height: "auto",
                objectFit: "contain",
                objectPosition: "center",
                borderRadius: "8px",
                display: "block",
              }}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default CashierPerson;
